import type { ChatMessage } from "@sill/shared/chat/types";

import type { ProRenderPayload } from "./pro-agent-types";

// IS_PREVIEW-only sample conversations for the Pro CRM Copilot dock
// (plan 188 W2-M9, task 808). These drive design-fidelity screenshots
// (scenarios 674-685) against synthetic render-frame data — no backend, no
// Clerk. Selected via a `?copilotDemo=<variant>` query param (see
// `useCopilotPreviewFixture`); never referenced on the authenticated path.

export type CopilotPreviewVariant =
  | "reply"
  | "confirm-message"
  | "confirm-message-suppressed"
  | "confirm-campaign"
  | "list-people"
  | "get-person"
  | "get-person-locked"
  | "campaign-results"
  | "audience-preview";

function nowIso(offsetMs = 0): string {
  return new Date(Date.now() - offsetMs).toISOString();
}

const REPLY_MESSAGES: ChatMessage<ProRenderPayload>[] = [
  {
    id: "fx-user-1",
    role: "user",
    text: "What needs my attention today?",
    createdAt: nowIso(4000),
  },
  {
    id: "fx-advisor-1",
    role: "advisor",
    text:
      "You have 3 clients going cold and 2 documents waiting on review. " +
      "Nina Park hasn't responded in 9 days — want me to draft a check-in?",
    followups: ["Draft a follow-up to Nina", "Who else is going cold?"],
    createdAt: nowIso(1000),
  },
];

const CONFIRM_MESSAGE_MESSAGES: ChatMessage<ProRenderPayload>[] = [
  {
    id: "fx-user-2",
    role: "user",
    text: "Draft a check-in to Nina Park",
    createdAt: nowIso(4000),
  },
  {
    id: "fx-advisor-2",
    role: "advisor",
    text: "Here's a draft — review it below before sending.",
    render: {
      type: "draft_message",
      contact_id: "aaaaaaaa-1111-4111-8111-111111111111",
      recipient_name: "Nina Park",
      recipient_email: "nina.park@example.com",
      subject: "Checking in on your home search",
      body:
        "Hi Nina,\n\nJust checking in — I know it's been a bit since we last " +
        "talked. Are you still looking in the Ballard area, or has anything " +
        "changed with your timeline? Happy to jump on a call this week.\n\nBest,\nDana",
      suppression: { suppressed: false },
    },
    renders: [
      {
        type: "draft_message",
        contact_id: "aaaaaaaa-1111-4111-8111-111111111111",
        recipient_name: "Nina Park",
        recipient_email: "nina.park@example.com",
        subject: "Checking in on your home search",
        body:
          "Hi Nina,\n\nJust checking in — I know it's been a bit since we last " +
          "talked. Are you still looking in the Ballard area, or has anything " +
          "changed with your timeline? Happy to jump on a call this week.\n\nBest,\nDana",
        suppression: { suppressed: false },
      },
    ],
    createdAt: nowIso(1000),
  },
];

const CONFIRM_MESSAGE_SUPPRESSED_MESSAGES: ChatMessage<ProRenderPayload>[] = [
  {
    id: "fx-user-3",
    role: "user",
    text: "Draft a check-in to Theo Park",
    createdAt: nowIso(4000),
  },
  {
    id: "fx-advisor-3",
    role: "advisor",
    text: "Here's a draft, but Theo has opted out of messages.",
    render: {
      type: "draft_message",
      contact_id: "bbbbbbbb-2222-4222-8222-222222222222",
      recipient_name: "Theo Park",
      recipient_email: "theo.park@example.com",
      subject: "Checking in on your home search",
      body:
        "Hi Theo,\n\nJust checking in on where things stand with your search. " +
        "Let me know if you'd like to reconnect.\n\nBest,\nDana",
      suppression: {
        suppressed: true,
        reason: "opted_out",
      },
    },
    renders: [
      {
        type: "draft_message",
        contact_id: "bbbbbbbb-2222-4222-8222-222222222222",
        recipient_name: "Theo Park",
        recipient_email: "theo.park@example.com",
        subject: "Checking in on your home search",
        body:
          "Hi Theo,\n\nJust checking in on where things stand with your search. " +
          "Let me know if you'd like to reconnect.\n\nBest,\nDana",
        suppression: {
          suppressed: true,
          reason: "opted_out",
        },
      },
    ],
    createdAt: nowIso(1000),
  },
];

const CONFIRM_CAMPAIGN_MESSAGES: ChatMessage<ProRenderPayload>[] = [
  {
    id: "fx-user-4",
    role: "user",
    text: "Draft a campaign to clients going cold",
    createdAt: nowIso(4000),
  },
  {
    id: "fx-advisor-4",
    role: "advisor",
    text: "I put together a draft campaign — nothing sends until you review it in Campaigns.",
    render: {
      type: "draft_campaign",
      campaign_id: "33333333-3333-4333-8333-333333333333",
      name: "Re-engage: going cold",
      audience_label: "All active clients · quiet 14+ days",
      recipient_count: 12,
      subject: "Still thinking about your next move?",
      body_preview:
        "Hi {{first_name}},\n\nIt's been a couple of weeks since we last connected. " +
        "I wanted to check in and see if anything has changed with your plans — " +
        "happy to catch up whenever works for you.",
    },
    renders: [
      {
        type: "draft_campaign",
        campaign_id: "33333333-3333-4333-8333-333333333333",
        name: "Re-engage: going cold",
        audience_label: "All active clients · quiet 14+ days",
        recipient_count: 12,
        subject: "Still thinking about your next move?",
        body_preview:
          "Hi {{first_name}},\n\nIt's been a couple of weeks since we last connected. " +
          "I wanted to check in and see if anything has changed with your plans — " +
          "happy to catch up whenever works for you.",
      },
    ],
    createdAt: nowIso(1000),
  },
];

// ── Tier-R read cards (SILL-454) ─────────────────────────────────────────────
// Each seeds one advisor turn whose render card is a faithful projection of a
// tool result — the anti-hallucination guard this ticket adds. The list cards
// are capped to 10 rows with a "View all" footer (truncated:true) even though
// the true total is higher, exactly like the live tool.

const LIST_PEOPLE_RENDER: ProRenderPayload = {
  type: "list_people",
  total: 24,
  truncated: true,
  people: [
    { contact_id: "c1", name: "Nina Park", sill_status: "connected", enrichment_state: "available", attention: 1, band: "Ready to act", actionable_reason: "Hasn't responded in 9 days" },
    { contact_id: "c2", name: "Marcus Lee", sill_status: "connected", enrichment_state: "available", attention: 2, band: "Getting close" },
    { contact_id: "c3", name: "Priya Raman", sill_status: "connected", enrichment_state: "available", attention: 2, band: "Ready to act" },
    { contact_id: "c4", name: "Dana Whitfield", sill_status: "connected", enrichment_state: "locked", attention: 3 },
    { contact_id: "c5", name: "Theo Alvarez", sill_status: "connected", enrichment_state: "available", attention: 3, band: "Getting close" },
    { contact_id: "c6", name: "Ravi Shah", sill_status: "joined_sill", enrichment_state: "not_connected", attention: 5 },
    { contact_id: "c7", name: "Elena Costa", sill_status: "invited", enrichment_state: "not_connected", attention: 6 },
    { contact_id: "c8", name: "Jon Meyers", sill_status: "not_invited", enrichment_state: "not_connected", attention: 8 },
    { contact_id: "c9", name: "Aisha Khan", sill_status: "invited", enrichment_state: "not_connected", attention: 6 },
    { contact_id: "c10", name: "Diego Ramos", sill_status: "not_invited", enrichment_state: "not_connected", attention: 8 },
  ],
};

const LIST_PEOPLE_MESSAGES: ChatMessage<ProRenderPayload>[] = [
  { id: "fx-user-5", role: "user", text: "Lay out my full book", createdAt: nowIso(4000) },
  {
    id: "fx-advisor-5",
    role: "advisor",
    text: "Here's your book — the first 10 of 24, sorted by who needs you most.",
    render: LIST_PEOPLE_RENDER,
    renders: [LIST_PEOPLE_RENDER],
    createdAt: nowIso(1000),
  },
];

// get_person — the single-person read card. Two variants because
// enrichment_state is the DISCRIMINATOR (design §3.2/D3): `available` and
// `locked` are both CONNECTED people, and they must render differently —
// `locked` is an upgrade prompt, and must never read as "hasn't connected".
const GET_PERSON_RENDER: ProRenderPayload = {
  type: "get_person",
  contact_id: "c1",
  name: "Nina Park",
  email: "nina.park@example.com",
  phone: "(206) 555-0148",
  area: "Ballard, Seattle",
  horizon: "3-6 months",
  tags: ["open house", "referral"],
  sill_status: "connected",
  enrichment_state: "available",
  // A real WIRE value (model.FinancialVerificationTier), not a display label:
  // pro_book.go assigns the enum verbatim. The card maps it to "Verified".
  verification_tier: "connected",
  readiness_label: "Ready to act",
  affordability_band_low: 620000,
  affordability_band_high: 685000,
};

const GET_PERSON_MESSAGES: ChatMessage<ProRenderPayload>[] = [
  { id: "fx-user-9", role: "user", text: "Tell me about Nina Park", createdAt: nowIso(4000) },
  {
    id: "fx-advisor-9",
    role: "advisor",
    text: "Here's Nina — connected, verified, and ready to act.",
    render: GET_PERSON_RENDER,
    renders: [GET_PERSON_RENDER],
    createdAt: nowIso(1000),
  },
];

// Connected, but this pro's plan doesn't include the readiness detail. The
// connected-only fields are ABSENT (they are populated only when
// enrichment_state === "available"), so the card cannot show a magnitude the
// lens omits.
const GET_PERSON_LOCKED_RENDER: ProRenderPayload = {
  type: "get_person",
  contact_id: "c4",
  name: "Dana Whitfield",
  area: "Fremont, Seattle",
  sill_status: "connected",
  enrichment_state: "locked",
};

const GET_PERSON_LOCKED_MESSAGES: ChatMessage<ProRenderPayload>[] = [
  { id: "fx-user-10", role: "user", text: "What's Dana's readiness?", createdAt: nowIso(4000) },
  {
    id: "fx-advisor-10",
    role: "advisor",
    text:
      "Dana is connected, but the readiness view isn't part of your current " +
      "plan — upgrading unlocks it.",
    render: GET_PERSON_LOCKED_RENDER,
    renders: [GET_PERSON_LOCKED_RENDER],
    createdAt: nowIso(1000),
  },
];

const CAMPAIGN_RESULTS_RENDER: ProRenderPayload = {
  type: "get_campaign_results",
  campaign_id: "33333333-3333-4333-8333-333333333333",
  recipient_count: 40,
  sent_count: 34,
  failed_count: 2,
  suppressed_count: 4,
};

const CAMPAIGN_RESULTS_MESSAGES: ChatMessage<ProRenderPayload>[] = [
  { id: "fx-user-7", role: "user", text: "How did my last campaign do?", createdAt: nowIso(4000) },
  {
    id: "fx-advisor-7",
    role: "advisor",
    text: "Here's the delivery breakdown — status only, there's no open/click tracking yet.",
    render: CAMPAIGN_RESULTS_RENDER,
    renders: [CAMPAIGN_RESULTS_RENDER],
    createdAt: nowIso(1000),
  },
];

const AUDIENCE_PREVIEW_RENDER: ProRenderPayload = {
  type: "preview_campaign_audience",
  matched: 52,
  eligible: 44,
  excluded_by_reason: { muted: 5, no_email: 3 },
};

const AUDIENCE_PREVIEW_MESSAGES: ChatMessage<ProRenderPayload>[] = [
  { id: "fx-user-8", role: "user", text: "Who would a going-cold campaign reach?", createdAt: nowIso(4000) },
  {
    id: "fx-advisor-8",
    role: "advisor",
    text: "That filter would reach 44 eligible people. Here's the breakdown.",
    render: AUDIENCE_PREVIEW_RENDER,
    renders: [AUDIENCE_PREVIEW_RENDER],
    createdAt: nowIso(1000),
  },
];

export const COPILOT_PREVIEW_FIXTURES: Record<
  CopilotPreviewVariant,
  ChatMessage<ProRenderPayload>[]
> = {
  reply: REPLY_MESSAGES,
  "confirm-message": CONFIRM_MESSAGE_MESSAGES,
  "confirm-message-suppressed": CONFIRM_MESSAGE_SUPPRESSED_MESSAGES,
  "confirm-campaign": CONFIRM_CAMPAIGN_MESSAGES,
  "list-people": LIST_PEOPLE_MESSAGES,
  "get-person": GET_PERSON_MESSAGES,
  "get-person-locked": GET_PERSON_LOCKED_MESSAGES,
  "campaign-results": CAMPAIGN_RESULTS_MESSAGES,
  "audience-preview": AUDIENCE_PREVIEW_MESSAGES,
};

export function isCopilotPreviewVariant(
  value: string | null,
): value is CopilotPreviewVariant {
  return !!value && value in COPILOT_PREVIEW_FIXTURES;
}
